(() => {
  const doc = document;
  const body = doc.body;

  doc.documentElement.classList.remove('no-js');

  const moneyFormat = window.PawlyTheme?.moneyFormat || '${{amount}}';
  const formatMoney = (cents) => {
    if (window.Shopify?.formatMoney) return window.Shopify.formatMoney(cents, moneyFormat);
    const value = Number(cents || 0) / 100;
    return new Intl.NumberFormat(document.documentElement.lang || 'fr-FR', {
      style: 'currency',
      currency: window.PawlyTheme?.currency || 'EUR'
    }).format(value);
  };

  const drawer = doc.querySelector('[data-mobile-drawer]');
  const menuButton = doc.querySelector('[data-menu-toggle]');
  const closeDrawer = () => {
    if (!drawer || !menuButton) return;
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    menuButton.setAttribute('aria-expanded', 'false');
    body.classList.remove('menu-open');
    menuButton.focus();
  };
  const openDrawer = () => {
    if (!drawer || !menuButton) return;
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    menuButton.setAttribute('aria-expanded', 'true');
    body.classList.add('menu-open');
    drawer.querySelector('a,button')?.focus();
  };
  menuButton?.addEventListener('click', () => drawer?.classList.contains('is-open') ? closeDrawer() : openDrawer());
  drawer?.querySelectorAll('[data-drawer-close]').forEach((el) => el.addEventListener('click', closeDrawer));
  doc.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && drawer?.classList.contains('is-open')) closeDrawer();
  });

  if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches && window.PawlyTheme?.animations !== false) {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.12 });
    doc.querySelectorAll('.reveal').forEach((el) => observer.observe(el));
  } else {
    doc.querySelectorAll('.reveal').forEach((el) => el.classList.add('is-visible'));
  }

  const parallaxMedia = doc.querySelector('[data-parallax]');
  if (parallaxMedia && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    let ticking = false;
    const updateParallax = () => {
      const rect = parallaxMedia.parentElement.getBoundingClientRect();
      const shift = Math.max(-40, Math.min(40, -rect.top * 0.06));
      parallaxMedia.style.transform = `scale(1.06) translate3d(0, ${shift}px, 0)`;
      ticking = false;
    };
    window.addEventListener('scroll', () => {
      if (!ticking) {
        window.requestAnimationFrame(updateParallax);
        ticking = true;
      }
    }, { passive: true });
    updateParallax();
  }

  doc.querySelectorAll('[data-carousel]').forEach((carousel) => {
    const track = carousel.querySelector('[data-carousel-track]');
    const previous = carousel.querySelector('[data-carousel-prev]');
    const next = carousel.querySelector('[data-carousel-next]');
    const move = (direction) => track?.scrollBy({ left: direction * Math.max(280, track.clientWidth * .72), behavior: 'smooth' });
    previous?.addEventListener('click', () => move(-1));
    next?.addEventListener('click', () => move(1));
  });

  doc.querySelectorAll('[data-quantity]').forEach((wrapper) => {
    const input = wrapper.querySelector('input[type="number"]');
    wrapper.querySelectorAll('button').forEach((button) => {
      button.addEventListener('click', () => {
        const step = Number(input.step || 1);
        const min = Number(input.min || 1);
        const nextValue = Number(input.value || min) + (button.dataset.quantityAction === 'increase' ? step : -step);
        input.value = Math.max(min, nextValue);
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });
  });

  doc.querySelectorAll('[data-product-section]').forEach((section) => {
    const variantSelect = section.querySelector('[data-variant-select]');
    const price = section.querySelector('[data-product-price]');
    const compare = section.querySelector('[data-product-compare]');
    const saleBadge = section.querySelector('[data-product-sale]');
    const addButton = section.querySelector('[data-add-to-cart]');
    const stickyPrice = doc.querySelector('[data-sticky-price]');
    const stickyButton = doc.querySelector('[data-sticky-submit]');
    const form = section.querySelector('form[action*="/cart/add"]');

    const updateVariant = () => {
      const option = variantSelect?.selectedOptions?.[0];
      if (!option) return;
      const available = option.dataset.available === 'true';
      const formattedPrice = option.dataset.price || formatMoney(option.dataset.priceCents);
      if (price) price.textContent = formattedPrice;
      if (stickyPrice) stickyPrice.textContent = formattedPrice;
      if (compare) {
        const comparePrice = option.dataset.compare;
        compare.textContent = comparePrice || '';
        compare.hidden = !comparePrice;
      }
      if (saleBadge) saleBadge.hidden = !option.dataset.compare;
      if (addButton) {
        addButton.disabled = !available;
        addButton.textContent = available ? addButton.dataset.availableLabel : addButton.dataset.soldoutLabel;
      }
      if (stickyButton) stickyButton.disabled = !available;
      const url = new URL(window.location.href);
      url.searchParams.set('variant', option.value);
      window.history.replaceState({}, '', url);
    };
    variantSelect?.addEventListener('change', updateVariant);
    updateVariant();
    stickyButton?.addEventListener('click', () => form?.requestSubmit());

    const mediaItems = section.querySelectorAll('[data-media-item]');
    const thumbnails = section.querySelectorAll('[data-media-thumbnail]');
    const activateMedia = (id) => {
      mediaItems.forEach((item) => item.classList.toggle('is-active', item.dataset.mediaItem === id));
      thumbnails.forEach((thumb) => {
        const active = thumb.dataset.mediaThumbnail === id;
        thumb.classList.toggle('is-active', active);
        thumb.setAttribute('aria-current', active ? 'true' : 'false');
      });
    };
    thumbnails.forEach((thumb) => thumb.addEventListener('click', () => activateMedia(thumb.dataset.mediaThumbnail)));

    const modal = section.querySelector('[data-media-modal]');
    const modalImage = modal?.querySelector('img');
    const closeModal = () => {
      if (!modal) return;
      modal.hidden = true;
      body.classList.remove('menu-open');
    };
    section.querySelectorAll('[data-zoom-image]').forEach((button) => {
      button.addEventListener('click', () => {
        if (!modal || !modalImage) return;
        modalImage.src = button.dataset.zoomImage;
        modalImage.alt = button.dataset.zoomAlt || '';
        modal.hidden = false;
        body.classList.add('menu-open');
        modal.querySelector('button')?.focus();
      });
    });
    modal?.querySelectorAll('[data-modal-close]').forEach((el) => el.addEventListener('click', closeModal));
    doc.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && modal && !modal.hidden) closeModal();
    });
  });
})();
